/**
 * Mutation hook for adding a new Item Price from the Pricing Card.
 *
 * Calls the thin backend endpoint which delegates all validation
 * to ERPNext's native Item Price before_save hook.
 *
 * Part of Plan 170 (Item Pricing Table Redesign), Epic 03.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { pricingSummaryKeys } from "./pricingSummaryHooks";

// ── Types ────────────────────────────────────────────────────

interface AddItemPriceArgs {
  item_code: string;
  price_list: string;
  rate: number;
  uom?: string;
  valid_from?: string;
  valid_upto?: string;
}

interface AddItemPriceResponse {
  name: string;
  item_code: string;
  price_list: string;
  price_list_rate: number;
  currency: string;
}

// ── Hook ─────────────────────────────────────────────────────

export function useAddItemPrice(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<AddItemPriceResponse, Error, AddItemPriceArgs>({
    mutationFn: (args) =>
      frappeMethod<AddItemPriceResponse>(
        "zaviago_backend.api.pricing_table.add_item_price",
        args
      ),
    onSuccess: () => {
      // Partial prefix match invalidates all customerGroup variants
      queryClient.invalidateQueries({
        queryKey: ["itemPricingTable", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: pricingSummaryKeys.summary(itemCode),
      });
      queryClient.invalidateQueries({
        queryKey: pricingSummaryKeys.allItemPrices(itemCode),
      });
    },
  });
}
